import { useState } from "react"
import { useTranslation } from "react-i18next"
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay"
import { Info, Warning } from "@phosphor-icons/react"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import {
  surveyLabQuestionnaireQuery as SurveyLabQuestionnaireQuery,
  surveyLabQuestionnaireQuery$data as SurveyLabQuestionnaireQuery$data,
} from "@/modules/patients/detail/progress-note/components/questionnaires/__generated__/surveyLabQuestionnaireQuery.graphql"
import { RelayEnvironment } from "@/relay-environment"

export const surveyLabQuestionnaireQuery = graphql`
  query surveyLabQuestionnaireQuery($appointmentId: ID!) {
    surveyLabQuestionnaireSummaryByAppointmentId(appointmentId: $appointmentId) {
      suggestedMedications
      questionAndAnswers {
        comment
        description
        question
        type
        userAnswer {
          number
          scoreLevel
          freeText
          openAnswers {
            label
            open
          }
          selectedAnswers {
            label
            open
          }
        }
        proposedAnswers {
          label
        }
      }
      telemedicineClassifier {
        aiReasoning
        aiRecommendation
      }
    }
  }
`

export const fetchSurveyLabQuestionnaire = async (appointmentId: string) => {
  return fetchQuery<SurveyLabQuestionnaireQuery>(RelayEnvironment, surveyLabQuestionnaireQuery, {
    appointmentId,
  }).toPromise()
}

type SurveyLabQuestionnaireType = NonNullable<
  SurveyLabQuestionnaireQuery$data["surveyLabQuestionnaireSummaryByAppointmentId"]
>["questionAndAnswers"][number]

type SurveyLabQuestionnaireProps = {
  appointmentId: string
}

const getCriticalityAnswerType = (questionnaire: SurveyLabQuestionnaireType) => {
  const scoreLevel = questionnaire.userAnswer?.scoreLevel
  switch (scoreLevel) {
    case "CRITICAL":
      return {
        backgroundClassName: "bg-critical-surface",
        answerClassName: "text-on-critical-surface",
        iconCriticalityAnswer: <Warning weight="fill" className="text-on-critical-surface" />,
      }
    case "WARNING":
      return {
        backgroundClassName: "bg-warning-surface-subdued",
        answerClassName: "text-on-warning-surface",
        iconCriticalityAnswer: <Warning className="text-on-warning-surface" />,
      }
    case "INFO":
      return {
        backgroundClassName: "bg-surface-subdued",
        answerClassName: "text-on-surface-subdued",
        iconCriticalityAnswer: <Info className="text-on-surface-subdued" />,
      }
    case null:
    case undefined:
    case "REVIEW":
      return {
        backgroundClassName: "",
        answerClassName: "",
        iconCriticalityAnswer: null,
      }
  }
}

export const getSurveyLabUserAnswers = (questionnaire: SurveyLabQuestionnaireType) => {
  const rawAnswer = questionnaire.userAnswer
  switch (questionnaire.type) {
    case "NUMERIC":
    case "RATING":
    case "SLIDER":
      return rawAnswer?.number ? [{ label: `${rawAnswer.number}`, displayText: `${rawAnswer.number}` }] : []
    case "FREE_TEXT": {
      const openAnswers = rawAnswer?.openAnswers
      if (openAnswers?.length) {
        return openAnswers.map((openAnswer) => ({
          label: openAnswer.label,
          displayText: joinStrings([openAnswer.label, openAnswer.open], ": "),
        }))
      }
      return rawAnswer?.freeText ? [{ label: rawAnswer.freeText, displayText: rawAnswer.freeText }] : []
    }
    case "SELECTION":
      return (
        rawAnswer?.selectedAnswers?.map((i) => {
          return { label: i.label, displayText: joinStrings([i.label, i?.open], ": ") }
        }) ?? []
      )
    default:
      return []
  }
}

export const SurveyLabQuestionnaire = ({ appointmentId }: SurveyLabQuestionnaireProps) => {
  const data = useLazyLoadQuery<SurveyLabQuestionnaireQuery>(surveyLabQuestionnaireQuery, { appointmentId })
  const [showReasoning, setShowReasoning] = useState(false)
  const { t } = useTranslation()

  const { surveyLabQuestionnaireSummaryByAppointmentId } = data
  const { questionAndAnswers: questionnaires, telemedicineClassifier: tmc } =
    surveyLabQuestionnaireSummaryByAppointmentId || {}

  return (
    <>
      <div className="flex items-center justify-between">
        {tmc && (
          <div data-testid="ai-recommendation" className="flex flex-col py-1">
            <p className="text-on-surface text-sm">
              {t(`patient.questionnaire.aiRecommendation.${tmc.aiRecommendation}`)}
            </p>
            {tmc.aiReasoning.length > 0 && (
              <>
                {showReasoning && <p className="text-on-surface text-sm leading-5">{tmc.aiReasoning}</p>}
                <button
                  className="text-primary w-fit cursor-pointer text-xs leading-4 tracking-[0.25px]"
                  onClick={() => setShowReasoning((prevState) => !prevState)}>
                  {showReasoning ? t("patient.questionnaire.hideReasoning") : t("patient.questionnaire.showReasoning")}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div className="mt-8 flex flex-col gap-4">
        {!!questionnaires?.length &&
          questionnaires.map((questionnaire) => (
            <QuestionnaireGroup key={questionnaire.question} questionnaire={questionnaire} />
          ))}
      </div>
    </>
  )
}

type QuestionnaireGroupProps = {
  questionnaire: SurveyLabQuestionnaireType
}
export const QuestionnaireGroup = ({ questionnaire }: QuestionnaireGroupProps) => {
  const { comment, description, question, proposedAnswers, type } = questionnaire
  const { backgroundClassName, answerClassName, iconCriticalityAnswer } = getCriticalityAnswerType(questionnaire)
  const userAnswers = getSurveyLabUserAnswers(questionnaire)

  const displayAnswers = userAnswers.map((i) => i.displayText)
  // Free-text answers are user-entered, not tied to proposed answers, so the long form
  // shows every user answer (all open answers) rather than proposed options.
  const isFreeFormAnswer = type === "FREE_TEXT"
  let displayAnswersLongForm = isFreeFormAnswer
    ? displayAnswers
    : proposedAnswers
        .map((i) => {
          const label = i.label?.trim() ?? ""
          // Combine with user selected answer.
          return userAnswers.find((a) => a.label === label)?.displayText ?? label
        })
        .filter(Boolean)
  if (displayAnswersLongForm.length === 0) {
    displayAnswersLongForm = displayAnswers
  }

  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <div
          className={`grid grid-cols-[24px_minmax(10px,1fr)_minmax(10px,1fr)] gap-2 rounded-lg p-2 ${backgroundClassName}`}>
          <span>{iconCriticalityAnswer}</span>
          <div className={cn("pt-0.5 text-xs wrap-break-word", answerClassName)}>
            <PotentiallyUnsafeInnerHtml value={description || question} />
          </div>
          <div className="flex flex-col">
            {displayAnswers && <Answers answers={displayAnswers} className={answerClassName} />}
            {comment && <Hint comment={comment} className={`${answerClassName} mt-0.5 border-t pt-0.5`} />}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="center" className="py-2">
          {(question || displayAnswersLongForm) && (
            <div className="p-1 leading-5">
              {question && <PotentiallyUnsafeInnerHtml value={question} />}
              {displayAnswersLongForm && (
                <ul className="ml-8 list-disc">
                  {displayAnswersLongForm.map((answer) => (
                    <li key={answer}>{answer}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

type AnswersProps = {
  answers: string[]
  className?: string
}
const Answers = ({ answers, className }: AnswersProps) => {
  return (
    <div className={cn("text-on-surface text-sm leading-5", className)}>
      {answers.map((text) => (
        <p key={text}>{text}</p>
      ))}
    </div>
  )
}

type HintProps = {
  comment: string
  className?: string
}
const Hint = ({ comment, className }: HintProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("text-on-surface text-sm leading-5 font-medium", className)}>
      <p>{t("patient.questionnaire.hint")}:</p>
      <p>{comment}</p>
    </div>
  )
}
